import type * as React from 'react';
import { FormattedMessage } from 'react-intl';

import ApplicationLayoutBodyBanner from '@rio-cloud/rio-uikit/ApplicationLayoutBodyBanner';
import { UserSettingsDialog } from './UserSettingsDialog';
import type { IFrameWindow } from '../../../../configuration/types';
import { hideSettingsDialog, isMaintenanceMode } from '../../appSlice';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { config } from '../../../../config';
import { pageLogout, pageRedirect, sendMessageToIFrameWrapper } from '../../../common/iFrameHelper';
import { redirectToLogout } from '../../../../configuration/login/logout';
import { useCloseDialogOnEscape } from '../../common/closeDialogOnEscape';
import { getIsRioProdUserPool, getUserProfileId } from '../../../../configuration/login/loginSlice';
import { MinimalUserInfoDialogContent } from './MinimalUserInfoDialogContent';
import { Wrapper } from './Wrapper';
import { useFetchUserWithEmbedQuery } from '../../services/usersApi';

export const UserSettingsDialogWrapper: React.FC = () => {
    const isInMaintenanceMode = useAppSelector(isMaintenanceMode);
    const isRioProdUserPool = useAppSelector(getIsRioProdUserPool);
    const userProfileId = useAppSelector(getUserProfileId);

    const dispatch = useAppDispatch();

    // Causes high number of logs in Sentry. Since we have a quote there this is disabled for now.
    // useEffect(() => {
    //     const [, query] = window.location.hash.split('?');
    //     const params = Object.fromEntries(new URLSearchParams(query));
    //     logMessageToSentry('User menu dialog opened', {
    //         'component.version': params['component_version'] ? params['component_version'] : 'unknown',
    //     });
    // }, []);

    const closeDialog = () => {
        if ('parentIFrame' in window) {
            (window as IFrameWindow).parentIFrame.sendMessage({
                type: 'IFRAME_CLOSE_DIALOG',
            });
        } else {
            dispatch(hideSettingsDialog());
        }
    };

    useCloseDialogOnEscape({ isShowDialog: true, closeDialog: closeDialog });

    const banner = isInMaintenanceMode ? (
        <ApplicationLayoutBodyBanner className={'text-center'}>
            <FormattedMessage id={'intl-msg:maintenanceMode'} />
        </ApplicationLayoutBodyBanner>
    ) : null;

    const broadcastLogout = () => {
        if ('parentIFrame' in window) {
            const logoutURI = config.backend.LOGOUT_URI
                ? config.backend.LOGOUT_URI
                : 'https://auth.iam.rio.cloud/logout';
            sendMessageToIFrameWrapper(pageRedirect(logoutURI));
            sendMessageToIFrameWrapper(pageLogout()); // compatibility with old component
        } else {
            redirectToLogout();
        }
    };

    return (
        <Wrapper>
            <div className='modal-content'>
                {banner}
                {<ModalHeader closeDialog={closeDialog} />}
                <div className='modal-body padding-y-15 padding-x-20'>
                    {isRioProdUserPool ? <UserSettingsDialog /> : <MinimalUserInfoDialogContent />}
                </div>
                {/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: suppress out of convenience during migration to biome */}
                <div className='modal-footer' aria-label='dialog footer'>
                    <div className={'btn-toolbar display-flex justify-content-end margin-top--5 margin-bottom-15'}>
                        {userProfileId && <GoToSelectAccountButton userProfileId={userProfileId} />}
                        <button type='button' className='btn btn-muted' onClick={broadcastLogout}>
                            <span className='rioglyph rioglyph-logout' />
                            <FormattedMessage id={'intl-msg:popover.logout'} />
                        </button>
                    </div>
                </div>
            </div>
        </Wrapper>
    );
};

const ModalHeader = ({ closeDialog }: { closeDialog: () => void }) => {
    const isRioProdUserPool = useAppSelector(getIsRioProdUserPool);
    const inIframe = window.self !== window.top;

    return (
        <div className='modal-header padding-top-10 margin-right--2'>
            <div className='modal-header-text'>
                <div className='modal-header-title'>
                    {isRioProdUserPool ? (
                        <FormattedMessage id={'intl-msg:popover.link.profile'} />
                    ) : (
                        <FormattedMessage id={'intl-msg:minimalSettingsDialog.header'} />
                    )}
                </div>
            </div>
            {inIframe && (
                <div className='modal-header-buttons'>
                    <button
                        type='button'
                        className='btn btn-icon-only btn-muted modal-header-close close'
                        onClick={closeDialog}
                    >
                        {/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: suppress out of convenience during migration to biome */}
                        <span className='rioglyph rioglyph-remove' aria-label={'close dialog icon'} />
                    </button>
                </div>
            )}
        </div>
    );
};

const GoToSelectAccountButton = ({ userProfileId }: { userProfileId: string }) => {
    const { data } = useFetchUserWithEmbedQuery(userProfileId);

    return data?.embedded?.number_active_profiles &&
        typeof data.embedded.number_active_profiles === 'number' &&
        data.embedded.number_active_profiles > 1 ? (
        <a
            href={`https://auth.iam.rio.cloud/select-account?post_login_redirect_uri=${encodeURIComponent(document.referrer)}`}
            target={'_top'}
        >
            <button type={'button'} className={'btn btn-muted'}>
                <span className={'rioglyph rioglyph-truck-unit'} aria-hidden={'true'} />
                <FormattedMessage id={'intl-msg:settingsDialog.tab.accounts.buttonLabel'} />
            </button>
        </a>
    ) : null;
};
